import React, { useEffect, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { fetchReferralById, selectReferralLoading } from 'redux/referrals';
import { selectScreenSize } from 'redux/session/selectors';
import { SCREEN_SIZE } from 'types';
import { selectReferrals, loadMoreLoader } from 'redux/referrals';
import MobileDataTable from './mobileDataTable';
import { Loading, Pagination } from 'carbon-components-react';
import { EmptyStatePage } from 'components';
import NoRowData from '../NoRowData';
import { ListDataTable } from './listDataTable';

const PageSizes = [10, 20, 50];

export const ListView: React.FC<Props> = ({
  referralList,
  setReferralList,
  setOrderBy,
  selectedId,
  currentPage,
  pageSize,
  handlePageChange,
  isEnableReprocess,
  selectedRowRef,
  setSelectedRowCount
}: any) => {
  // Initialisation
  const table = {
    headerData: [
      ...(isEnableReprocess
        ? [{ key: 'isReprocess', header: 'Reprocess', isCheckbox: true }]
        : []),
      { key: 'externalId', header: 'Referral', isSortable: true },
      { key: 'job', header: 'Job', isSortable: true },
      { key: 'address', header: 'Address' },
      { key: 'updatedAt', header: 'Last Updated', isSortable: true },
      { key: 'enquirer', header: 'Enquirer', isSortable: true },
      { key: 'status', header: 'Status' }
    ]
  };
  const dispatch = useDispatch();
  // SELECTORS
  const isLoading: boolean = useSelector(selectReferralLoading);
  const loader: any = useSelector(loadMoreLoader);
  const screenSize = useSelector(selectScreenSize);
  const referrals: any = useSelector(selectReferrals);
  const [sortingHeader, setSortingHeader] = useState<string>('externalId');
  const [sortDirection, setSortDirection] = useState<string>('DESC');

  const onClickHeader = (key: string) => {
    const direction =
      key === sortingHeader
        ? sortDirection === 'DESC'
          ? 'ASC'
          : 'DESC'
        : 'DESC';
    let order = '';
    switch (key) {
      case 'job': {
        order = `${direction === 'DESC' ? '-' : ''}createdAt`;
        break;
      }
      case 'externalId': {
        order = `${direction === 'DESC' ? '-' : ''}externalId`;
        break;
      }
      case 'enquirer': {
        order = `${direction === 'DESC' ? '-' : ''}requestorFirstName,${
          direction === 'DESC' ? '-' : ''
        }requestorLastName`;
        break;
      }
      case 'updatedAt': {
        order = `${direction === 'DESC' ? '-' : ''}updatedAt`;
        break;
      }
    }
    setSortingHeader(key);
    setOrderBy(order);
    setSortDirection(direction);
  };

  useEffect(() => {
    if (referrals) {
      const newReferralList = [...referrals.items];
      setReferralList(newReferralList);
    }
  }, [referrals, setReferralList]);

  const referralDetailsHandler = useCallback(
    (selectRowObj: any) => {
      const { id } = selectRowObj;
      const referral = referralList.find((item: any) => item.id === id);
      const { externalId, enquiryId } = referral;
      if (id && enquiryId) {
        const payload = {
          enquiryId,
          referralId: id,
          returnResponses: true,
          returnGeometry: true
        };
        selectedId(externalId);
        dispatch(fetchReferralById(payload));
      }
    },
    [selectedId, referralList, dispatch]
  );
  return (
    <div
      id="referralHistoryListView"
      className="history-page--list-view referral-table"
      data-testid="referralHistoryListView"
    >
      {!isLoading && !(loader && referralList.length === 0) ? (
        <>
          {referrals?.count > 0 ? (
            <>
              {screenSize !== SCREEN_SIZE.SM &&
              screenSize !== SCREEN_SIZE.MD ? (
                <ListDataTable
                  size="tall"
                  isLoading={isLoading}
                  headerData={table.headerData}
                  rowData={referralList}
                  selectedRowHandler={referralDetailsHandler}
                  onClickHeader={onClickHeader}
                  sortingHeader={sortingHeader}
                  sortDirection={sortDirection}
                  selectedRowRef={selectedRowRef}
                  setSelectedRowCount={setSelectedRowCount}
                />
              ) : (
                <MobileDataTable
                  size="tall"
                  headers={table.headerData}
                  rows={referralList}
                  title={''}
                  description={''}
                  selectedRowHandler={referralDetailsHandler}
                />
              )}
              {referrals?.count > 0 && (
                <Pagination
                  onChange={handlePageChange}
                  backwardText="Previous page"
                  forwardText="Next page"
                  itemsPerPageText="Items per page:"
                  page={currentPage}
                  pageNumberText="Page Number"
                  pageSize={pageSize}
                  pageSizes={PageSizes}
                  totalItems={referrals.count}
                />
              )}
            </>
          ) : (
            <EmptyStatePage>
              <NoRowData />
            </EmptyStatePage>
          )}
        </>
      ) : (
        <div className="loader--wrapper">
          <Loading description="Active loading indicator" withOverlay={false} />
        </div>
      )}
    </div>
  );
};

const propTypes = {
  referralList: PropTypes.array.isRequired,
  setReferralList: PropTypes.func.isRequired,
  setOrderBy: PropTypes.func.isRequired,
  selectedId: PropTypes.func.isRequired,
  currentPage: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  handlePageChange: PropTypes.func.isRequired,
  isEnableReprocess: PropTypes.bool.isRequired,
  selectedRowRef: PropTypes.any.isRequired,
  setSelectedRowCount: PropTypes.func.isRequired
};

type Props = PropTypes.InferProps<typeof propTypes>;

ListView.propTypes = propTypes;

ListView.defaultProps = {
  selectedId: () => {},
  handlePageChange: () => {}
};
export default React.memo(ListView);
